import type { AutocompleteRenderGetTagProps, AutocompleteRenderValueGetItemProps } from '@mui/material';
import type { GridColDef, GridComparatorFn, GridRowGroupingModel, GridSortModel } from '@mui/x-data-grid-premium';
import isEqual from 'lodash/isEqual';
import keyBy from 'lodash/keyBy';
import pick from 'lodash/pick';
import { useRef } from 'react';
import type { I18nFunctionType } from '../../../../i18n/I18n';
import {
    EMPTY_GRID_GROUPING_MODEL,
    GRID_SETTINGS_CUSTOM_SORT_ID,
    GRID_SETTINGS_GROUPING_COLUMN_FIELD
} from '../constants';
import type {
    GridSettingsConfig,
    GridSettingsDraft,
    GridSettingsOption,
    GridSettingsPreference,
    GridSettingsSortField,
    GridSettingsSortOption
} from '../types';

const EMPTY_RANK_MAP = new Map<string, number>();

export function useStableReference<T>(value: T): T {
    const ref = useRef(value);
    if (!isEqual(ref.current, value)) {
        ref.current = value;
    }
    return ref.current;
}

export function createCombinedSortOption(
    field: string,
    direction: 'asc' | 'desc',
    label: string
): GridSettingsSortOption {
    return {
        id: `${field}_${direction}`,
        label,
        field,
        direction
    };
}

/** A `createCombinedSortOption` whose label is the localized "{field} Asc" / "{field} Desc". */
export function combinedOption(
    t: I18nFunctionType,
    field: string,
    direction: 'asc' | 'desc',
    fieldLabel: string
): GridSettingsSortOption {
    return createCombinedSortOption(
        field,
        direction,
        t(direction === 'asc' ? 'grid.settings.sort.combined.asc' : 'grid.settings.sort.combined.desc', {
            field: fieldLabel
        })
    );
}

export function createCustomSortOption(label: string): GridSettingsSortOption {
    return { id: GRID_SETTINGS_CUSTOM_SORT_ID, label };
}

export function isCustomSortId(sortBy: string | null | undefined): boolean {
    return sortBy === GRID_SETTINGS_CUSTOM_SORT_ID;
}

export function findSortOption(
    options: GridSettingsSortOption[] | undefined,
    id: string | null | undefined
): GridSettingsSortOption | undefined {
    if (!id || !options) {
        return undefined;
    }

    return options.find((option) => option.id === id);
}

export function hasCustomSortOption(config: GridSettingsConfig | undefined): boolean {
    return Boolean(config?.sortOptions.some((option) => isCustomSortId(option.id)));
}

export function groupedFieldsForFields(groupedFields: string[], fieldIds: string[]): string[] {
    const selected = new Set(fieldIds);
    return groupedFields.filter((fieldId) => selected.has(fieldId));
}

export function groupedFieldsWithToggle(
    sortFields: string[],
    groupedFields: string[],
    fieldId: string,
    checked: boolean
): string[] {
    const enabled = new Set(groupedFields);
    if (checked) {
        enabled.add(fieldId);
    } else {
        enabled.delete(fieldId);
    }

    return sortFields.filter((id) => enabled.has(id));
}

/** Older views stored group fields with no groupedFields list; those grouped every field. */
export function groupedFieldsFromPreference(preference: GridSettingsPreference): string[] {
    if (!isCustomSortId(preference.sortBy)) {
        return EMPTY_GRID_GROUPING_MODEL;
    }

    const selectedFields = preference.groupFields ?? [];
    if (preference.groupedFields) {
        return groupedFieldsForFields(preference.groupedFields, selectedFields);
    }

    return selectedFields;
}

export function areGridSettingsEqual(
    left?: GridSettingsPreference | null,
    right?: GridSettingsPreference | null
): boolean {
    return isEqual(left ?? {}, right ?? {});
}

export function toDefaultPreference(
    config: GridSettingsConfig | undefined,
    defaultSortModel?: GridSortModel,
    defaultRowGroupingModel?: GridRowGroupingModel
): GridSettingsPreference {
    if (defaultRowGroupingModel?.length && hasCustomSortOption(config)) {
        return {
            sortBy: GRID_SETTINGS_CUSTOM_SORT_ID,
            groupFields: [...defaultRowGroupingModel],
            inGroupSortBy: config?.inGroupSortOptions?.[0]?.id
        };
    }

    const defaultSort = defaultSortModel?.[0];
    if (defaultSort?.field && config?.sortOptions) {
        const match = config.sortOptions.find(
            (option) => option.field === defaultSort.field && option.direction === (defaultSort.sort ?? 'asc')
        );
        if (match) {
            return { sortBy: match.id };
        }
    }

    const firstPrimary = config?.sortOptions.find((option) => !isCustomSortId(option.id));
    return firstPrimary ? { sortBy: firstPrimary.id } : {};
}

function sortModelFromOption(option: GridSettingsSortOption | undefined): GridSortModel | undefined {
    if (!option?.field) {
        return undefined;
    }

    return [{ field: option.field, sort: option.direction ?? 'asc' }];
}

export function preferenceToSortModel(
    preference: GridSettingsPreference,
    config: GridSettingsConfig | undefined
): GridSortModel | undefined {
    if (isCustomSortId(preference.sortBy)) {
        if (preference.groupFields?.length) {
            if (groupedFieldsFromPreference(preference).length) {
                return [{ field: GRID_SETTINGS_GROUPING_COLUMN_FIELD, sort: 'asc' }];
            }

            return [{ field: preference.groupFields[0], sort: 'asc' }];
        }

        return sortModelFromOption(findSortOption(config?.inGroupSortOptions, preference.inGroupSortBy));
    }

    return sortModelFromOption(findSortOption(config?.sortOptions, preference.sortBy));
}

export function preferenceToRowGroupingModel(preference: GridSettingsPreference): GridRowGroupingModel {
    return groupedFieldsFromPreference(preference);
}

export function displayedValueOrder(options: GridSettingsOption[]): string[] {
    return [...options].sort((left, right) => comparePrimitiveValues(left.id, right.id)).map((option) => option.id);
}

/**
 * A saved view can sort without a stored value order. The grid then shows values in
 * display order, so the settings form should start with that same order selected.
 * An explicit empty order is a cleared selection and stays empty.
 */
export function prefillEmptyValueOrders(
    preference: GridSettingsPreference,
    sortFields: GridSettingsSortField[]
): GridSettingsPreference {
    if (!isCustomSortId(preference.sortBy) || !preference.groupFields?.length) {
        return preference;
    }

    const valueOrders = { ...(preference.groupValueOrders ?? {}) };
    let changed = false;

    for (const fieldId of preference.groupFields) {
        if (Array.isArray(valueOrders[fieldId])) {
            continue;
        }

        const field = sortFields.find((sortField) => sortField.id === fieldId);
        const nextOrder = displayedValueOrder(field?.valueOptions ?? []);
        if (!nextOrder.length) {
            continue;
        }

        valueOrders[fieldId] = nextOrder;
        changed = true;
    }

    return changed ? { ...preference, groupValueOrders: valueOrders } : preference;
}

export function toGridSettingsDraft(
    preference: GridSettingsPreference,
    autosizeEnabled: boolean,
    sortFields: GridSettingsSortField[] = []
): GridSettingsDraft {
    const resolvedPreference = prefillEmptyValueOrders(preference, sortFields);

    return {
        sortBy: resolvedPreference.sortBy ?? null,
        sortFields: resolvedPreference.groupFields ?? [],
        valueOrders: resolvedPreference.groupValueOrders ?? {},
        inGroupSortBy: resolvedPreference.inGroupSortBy ?? null,
        groupedFields: groupedFieldsFromPreference(resolvedPreference),
        autosizeEnabled
    };
}

export function hasCustomConfiguration(draft: GridSettingsDraft): boolean {
    return draft.sortFields.length > 0 || Object.values(draft.valueOrders).some((values) => (values || []).length > 0);
}

export function toGridSettingsPreference(draft: GridSettingsDraft): GridSettingsPreference {
    if (!isCustomSortId(draft.sortBy)) {
        return { sortBy: draft.sortBy ?? undefined };
    }

    const groupedFields = groupedFieldsForFields(draft.groupedFields, draft.sortFields);
    const allFieldsGrouped = groupedFields.length > 0 && groupedFields.length === draft.sortFields.length;

    return {
        sortBy: GRID_SETTINGS_CUSTOM_SORT_ID,
        groupFields: draft.sortFields,
        groupValueOrders: draft.valueOrders,
        inGroupSortBy: draft.inGroupSortBy ?? undefined,
        ...(allFieldsGrouped ? {} : { groupedFields })
    };
}

export function toSelectOptions(options: GridSettingsOption[]): GridSettingsOption[] {
    return options.map(({ id, label }) => ({ id, label }));
}

export function sortFieldsForIds(sortFields: GridSettingsSortField[], ids: string[]): GridSettingsSortField[] {
    const sortFieldsById = keyBy(sortFields, 'id');
    return ids.map((id) => sortFieldsById[id]).filter((field): field is GridSettingsSortField => Boolean(field));
}

export function hasSelectedValueOrder(
    valueOrders: Record<string, string[]>,
    sortFields: GridSettingsSortField[]
): boolean {
    return sortFields.some((sortField) => (valueOrders[sortField.id] || []).length > 0);
}

export function valueOrdersForFields(
    valueOrders: Record<string, string[]>,
    fieldIds: string[]
): Record<string, string[]> {
    return pick(valueOrders, fieldIds);
}

export function toOptionId(selectedValue: unknown): string | null {
    if (typeof selectedValue === 'string' && selectedValue.length > 0) {
        return selectedValue;
    }

    if (!selectedValue || typeof selectedValue !== 'object') {
        return null;
    }

    return toOptionIds([selectedValue])[0] ?? null;
}

export function toOptionIds(selectedValue: unknown): string[] {
    if (!Array.isArray(selectedValue)) {
        return [];
    }

    return selectedValue
        .map((value) => {
            if (typeof value === 'string') {
                return value;
            }
            if (value && typeof value === 'object') {
                if ('id' in value && typeof value.id === 'string') {
                    return value.id;
                }
                if ('label' in value && typeof value.label === 'string') {
                    return value.label;
                }
            }
            return undefined;
        })
        .filter((value): value is string => Boolean(value));
}

export function optionsForIds(ids: string[], options: GridSettingsOption[]): GridSettingsOption[] {
    const byId = new Map(options.map((option) => [option.id, option]));
    return ids.map((id) => byId.get(id) ?? { id, label: id });
}

export function toSortableChipTagProps(
    getItemProps: AutocompleteRenderValueGetItemProps<true>
): AutocompleteRenderGetTagProps {
    return function getTagProps({ index }) {
        const itemProps = getItemProps({ index });
        return {
            key: itemProps.key,
            className: itemProps.className,
            disabled: itemProps.disabled,
            'data-tag-index': itemProps['data-item-index'],
            tabIndex: itemProps.tabIndex,
            onDelete: itemProps.onDelete
        };
    };
}

export function toFieldValueId(value: unknown): string | undefined {
    if (value == null || value === '') {
        return undefined;
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    if (typeof value === 'object' && 'label' in value && typeof value.label === 'string') {
        return value.label;
    }

    return String(value);
}

export function uniqueFieldValues(
    rows: readonly Record<string, unknown>[] | undefined,
    field: string
): GridSettingsOption[] {
    if (!rows?.length) {
        return [];
    }

    const seen = new Set<string>();
    const options: GridSettingsOption[] = [];

    for (const row of rows) {
        const id = toFieldValueId(row[field]);
        if (!id || seen.has(id)) {
            continue;
        }

        seen.add(id);
        options.push({ id, label: id });
    }

    return options;
}

export function resolveSortFields(
    sortFields: GridSettingsSortField[] | undefined,
    rows: readonly Record<string, unknown>[] | undefined
): GridSettingsSortField[] {
    if (!sortFields) {
        return [];
    }

    return sortFields.map((sortField) => {
        if (sortField.valueOptions || sortField.metadataName) {
            return sortField;
        }

        return {
            ...sortField,
            valueOptions: uniqueFieldValues(rows, sortField.id)
        };
    });
}

function normalizeSortValue(value: unknown): string {
    return toFieldValueId(value)?.trim().toLowerCase() ?? '';
}

function createRankMap(order: string[]): Map<string, number> {
    const rankMap = new Map(order.map((value, index) => [normalizeSortValue(value), index]));
    return rankMap;
}

function createRankMapsByField(
    fields: string[],
    valueOrders: Record<string, string[]> | undefined
): Map<string, Map<string, number>> {
    const rankMapsByField = new Map<string, Map<string, number>>();
    for (const field of fields) {
        rankMapsByField.set(field, createRankMap(valueOrders?.[field] ?? []));
    }
    return rankMapsByField;
}

function compareByRankMap(left: unknown, right: unknown, rank: Map<string, number>): number {
    const leftRank = rank.get(normalizeSortValue(left)) ?? Number.MAX_SAFE_INTEGER;
    const rightRank = rank.get(normalizeSortValue(right)) ?? Number.MAX_SAFE_INTEGER;

    if (leftRank !== rightRank) {
        return leftRank - rightRank;
    }

    const comparison = comparePrimitiveValues(left, right);
    return comparison;
}

export function compareByRankOrder(left: unknown, right: unknown, order: string[]): number {
    const comparison = compareByRankMap(left, right, createRankMap(order));
    return comparison;
}

export function comparePrimitiveValues(left: unknown, right: unknown): number {
    if ((left == null || left === '') && (right == null || right === '')) {
        return 0;
    }
    if (left == null || left === '') {
        return 1;
    }
    if (right == null || right === '') {
        return -1;
    }
    if (typeof left === 'number' && typeof right === 'number') {
        return left - right;
    }

    return String(left).localeCompare(String(right), undefined, { sensitivity: 'base', numeric: true });
}

/**
 * Always sets `sortComparator` so MUI's column merge can replace a previous custom
 * comparator. Omitting the key leaves the last custom rank order on the column.
 */
export function withFlatSortComparator(
    column: GridColDef,
    flatSortField?: string,
    flatSortComparator?: GridComparatorFn
): GridColDef {
    const sortComparator =
        flatSortField && flatSortComparator && column.field === flatSortField
            ? flatSortComparator
            : column.sortComparator;

    return {
        ...column,
        sortComparator
    };
}

export function createFlatSortComparator(
    sortFields: string[],
    valueOrders: Record<string, string[]> | undefined,
    inGroupOption: GridSettingsSortOption | undefined
): GridComparatorFn {
    const rankMapsByField = createRankMapsByField(sortFields, valueOrders);

    return function compareFlatRows(_left, _right, cellParams1, cellParams2) {
        const row1 = cellParams1.api.getRow(cellParams1.id);
        const row2 = cellParams2.api.getRow(cellParams2.id);

        for (const field of sortFields) {
            const comparison = compareByRankMap(
                row1?.[field],
                row2?.[field],
                rankMapsByField.get(field) ?? EMPTY_RANK_MAP
            );
            if (comparison !== 0) {
                return comparison;
            }
        }

        if (inGroupOption?.field) {
            const comparison = comparePrimitiveValues(row1?.[inGroupOption.field], row2?.[inGroupOption.field]);
            return inGroupOption.direction === 'desc' ? -comparison : comparison;
        }

        return 0;
    };
}

export function createGroupingSortComparator(
    valueOrders: Record<string, string[]> | undefined,
    inGroupOption: GridSettingsSortOption | undefined,
    ungroupedSortFields: string[] = []
): GridComparatorFn {
    const rankMapsByField = createRankMapsByField(
        [...Object.keys(valueOrders ?? {}), ...ungroupedSortFields],
        valueOrders
    );

    return function compareGroupingRows(left, right, cellParams1, cellParams2) {
        const node1 = cellParams1.rowNode;
        const node2 = cellParams2.rowNode;

        if (node1.type === 'group' && node2.type === 'group') {
            const groupingField = 'groupingField' in node1 ? node1.groupingField : null;
            const leftKey = 'groupingKey' in node1 ? node1.groupingKey : left;
            const rightKey = 'groupingKey' in node2 ? node2.groupingKey : right;
            const rankMap = groupingField ? (rankMapsByField.get(groupingField) ?? EMPTY_RANK_MAP) : EMPTY_RANK_MAP;
            const comparison = compareByRankMap(leftKey, rightKey, rankMap);
            return comparison;
        }

        if (node1.type !== 'group' && node2.type !== 'group') {
            const row1 = cellParams1.api.getRow(cellParams1.id);
            const row2 = cellParams2.api.getRow(cellParams2.id);

            for (const field of ungroupedSortFields) {
                const comparison = compareByRankMap(
                    row1?.[field],
                    row2?.[field],
                    rankMapsByField.get(field) ?? EMPTY_RANK_MAP
                );
                if (comparison !== 0) {
                    return comparison;
                }
            }

            if (inGroupOption?.field) {
                const comparison = comparePrimitiveValues(row1?.[inGroupOption.field], row2?.[inGroupOption.field]);
                return inGroupOption.direction === 'desc' ? -comparison : comparison;
            }
        }

        return 0;
    };
}
